/* eslint-disable @typescript-eslint/no-this-alias */
/* eslint-disable no-new, no-prototype-builtins, import/no-amd */
// eslint-disable-next-line import/no-extraneous-dependencies
import { normalizeAccents } from 'bam-node-baseball-savant/utils/normalize-accents'
import * as d3 from 'd3'

import {
    fastRewindIcon,
    rewindIcon,
    forwardIcon,
    fastForwardIcon,
} from './raw-svg-icons.ts'

export const SAVANT_TABLE_PAGINATION_RANK_PROP = 'savant-table-pagination-rank'

function setTextColorWithBackground(
    color: d3.RGBColor | d3.HSLColor | null
): string {
    if (!color) {
        return '#000'
    }

    if ('r' in color && 'g' in color && 'b' in color) {
        const { r, g, b } = color
        const colorValue = Math.round((r * 299 + g * 587 + b * 114) / 1000)

        return colorValue > 125 ? '#000' : '#fff'
    }

    return '#000'
}

export type SortDirection = 'asc' | 'desc'

export type FilterType =
    | 'in'
    | 'gt'
    | 'gte'
    | 'lt'
    | 'lte'
    | 'eq'
    | 'neq'
    | 'ilike'
    | 'not_null'

type Filter<Data> = {
    column: keyof Data
    type: FilterType
    value: (string | number)[] | string | number | boolean
}

// Basically the JSON types
export type RowValue = string | number | boolean | undefined | null

type Sort<Data> = { field: keyof Data; state: SortDirection }

export type TableRowBase = {
    name?: string
    season?: string
    playerId?: string
    pinned?: boolean
    [key: string]: RowValue
}

export type Header<Data extends TableRowBase> = {
    expanded?: boolean
    spanClass?:
        | string
        | {
              className: string
              exception: 'gte' | 'gt' | 'lte' | 'lt' | 'eq'
              field: keyof Data
              parameter: number
              includeFooter: boolean
          }[]
    ranking?: string
    photo?: string
    photoId?: keyof Data
    highlight?: number[]
    highlightColors?: string[]
    rankColumn?: string
    id?: string
    state?: SortDirection
    highlightDynamic?: unknown
    isLinkedSort?: unknown
    name: string
    field: keyof Data
    headerClass: string
    fieldClass: string
    displayFn?: (
        // Attempts to do better have been met with resistance. Be warned.
        val: RowValue,
        row: Data,
        header: Header<Data>,
        isFooter: boolean
    ) => string
    sortable?: boolean
    type?: string
    customSortField?: keyof Data
    defaultSortDir?: SortDirection
    toolTip?: string
    toolTipClass?: string
    hasLinkedSort?: boolean
    linkedSort?: string
    footerClass?: string
    photoOnly?: boolean
    fieldAlt?:
        | string
        | ((fieldData: RowValue, row: Data, header: Header<Data>) => string)
}

const defaultRowsPerPage = 50
const almostInfinity = 999999999

const pageOptions = [
    15, // A view that will probably fit on one vertical screen
    30, // Nice for teams?
    defaultRowsPerPage, // If you are used to having a lot of players on one page this is a performant version.
    100,
    200,
    almostInfinity,
] as const

// Magic string
const TABLE_SELECT_PAGE_SIZE_DATA_STRING = 'tablePaginationPageSize'

type PageOption = (typeof pageOptions)[number]

export type TableOpts<Data extends TableRowBase, TTable> = {
    drawerAvailable?: (row: Data) => boolean
    isSticky?: boolean
    footer?: Data[]
    highlightValFn?: (val: RowValue, row: Data, data: Data[]) => number
    highlightNullish?: boolean
    id?: string
    repeatHeader?: number
    headers?: Header<Data>[]
    secondaryHeaders?: { name: string; colspan: number; headerClass: string }[]
    hasPinned?: boolean
    highlightFalsy?: boolean
    data: Data[]
    uniqueId?: keyof Data
    defaultSort?: keyof Data | Sort<Data>[]
    initialSortDirection?: SortDirection
    sortClass?: string
    showRank?: boolean
    sortCallback?: (
        column: keyof Data,
        idx: number,
        header: Header<Data>,
        data: Data[],
        table: TTable
    ) => void
    subDataId?: string
    rowClass?: string
    tableWrapperClass?: string
    defaultSortDirection?: SortDirection
    footerClass?: string
    showPinned?: boolean
    handleUrlUpdates?: {
        enabled?: boolean
        pagination?: boolean
        search?: boolean
        sort?: boolean
        filters?: boolean
    }
    pagination?: {
        enabled?: boolean
        rowsPerPage?: PageOption
        page?: number
        pages?: number
    }
    search?: {
        enabled?: boolean
        columnsToSearch?: (keyof Data)[]
        searchString?: string
        placeholder?: string
    }
}

export function filterColumn<Data>(
    haystack: Data[],
    { column, type, value }: Filter<Data>
) {
    const isArray = Array.isArray(value)
    return haystack.filter((d) => {
        const val = d[column]

        if (val === undefined || val === null) {
            return false
        }

        if (type === 'in' && isArray) {
            if (typeof val === 'string' || typeof val === 'number') {
                return value.includes(val)
            }
            return false
        }

        if (type === 'eq' && !isArray) {
            return val === value
        }

        if (type === 'neq' && !isArray) {
            return val !== value
        }

        if (type === 'gte' && !isArray) {
            return val >= value
        }

        if (type === 'gt' && !isArray) {
            return val > value
        }

        if (type === 'lte' && !isArray) {
            return val <= value
        }

        if (type === 'lt' && !isArray) {
            return val < value
        }

        if (type === 'ilike' && !isArray) {
            return String(val)
                .toLowerCase()
                .includes(String(value).toLowerCase())
        }

        // The nulls are actually removed above
        if (type === 'not_null') {
            return true
        }

        return false
    })
}

export default class Table<Data extends TableRowBase> {
    highlightColor: d3.ScaleLinear<string, string, never>

    highlightRanking: number[] | d3.ScaleLinear<string, number, never>

    id: string = ''

    sel: HTMLElement

    data: Data[] = []

    filteredData: Data[] = []

    pinned: Data[] = []

    notPinned: Data[] = []

    headers: Header<Data>[] = []

    secondaryHeaders?: {
        name: string
        colspan: number
        headerClass: string
    }[] = []

    // Required?
    defaultSort: Sort<Data>[] = []

    defaultSortDirection: SortDirection = 'desc'

    initialSortDirection: SortDirection = 'desc'

    currentSortColumn: Sort<Data>[] = []

    subDataId?: keyof Data

    drawerAvailable?: (row: Data) => boolean

    uniqueId?: keyof Data

    showRank: boolean = true

    repeatHeader?: number

    hasPinned?: boolean

    isSticky?: boolean

    footer?: Data[]

    sortClass: string = 'column-sort'

    footerClass?: string

    sortCallback?: (
        column: keyof Data,
        idx: number,
        header: Header<Data>,
        data: Data[],
        table: this
    ) => void

    rowClass?: string

    // eslint-disable-next-line class-methods-use-this
    highlightValFn: (val: RowValue, row: Data, data: Data[]) => number = (
        val: RowValue
    ) => Number(val)

    highlightFalsy?: boolean

    highlightNullish?: boolean

    existsLinkedSort?: boolean

    secondaryHeaderHTML?: string

    primaryHeaderHTML?: string

    showFooterLinks?: boolean

    dynamicHighlightHeaders?: (keyof Data)[]

    hasDynamicHighlights?: boolean

    dynamicHighlightDomains?: { field: keyof Data; domain: number[] }[]

    showPinned: boolean = true

    tableWrapperClass: string = 'table-savant table-savant-loose'

    handleUrlUpdates: {
        enabled: boolean
        pagination: boolean
        search: boolean
        sort: boolean
        filters: boolean
    } = {
        enabled: false,
        pagination: false,
        search: false,
        sort: true,
        filters: true,
    }

    pagination: {
        enabled: boolean
        rowsPerPage: number
        page: number
        pages: number
    } = { enabled: true, rowsPerPage: defaultRowsPerPage, page: 0, pages: 1 }
    // To zero index page or not?

    search: {
        enabled: boolean
        columnsToSearch: (keyof Data)[]
        searchString: string
        placeholder: string
    } = {
        enabled: true,
        columnsToSearch: [],
        searchString: '',
        placeholder: 'Search for a Player...',
    }

    filters: Filter<Data>[] = []

    constructor(tableOpts: TableOpts<Data, Table<Data>>) {
        this.highlightColor = d3.scaleLinear<string>()

        this.highlightRanking = d3
            .scaleLinear<string, number>()
            .range(['#ff7777', '#ffffff'])
            .domain([1, 20])

        // Sets global tableOpts variables
        if (tableOpts.id) {
            this.id = tableOpts.id
            const sel = document.getElementById(tableOpts.id)

            if (!sel) {
                throw new Error(`Can't find id ${tableOpts.id}`)
            }

            this.sel = sel
            // Remove table-savant class in favor of wrapper inside
            this.sel.classList.remove('table-savant')
            this.sel.classList.add('table-savant-wrapper')
        } else {
            throw new Error('No id!')
        }
        this.initOpts(tableOpts)
        // Initiliazes table and draws it
        this.initTable()
    }

    initTable() {
        // this.tableHeader += '</thead><tbody>'
        this.showFooterLinks = false
        this.currentSortColumn = []

        // If dynamically highlighted columns exist, identify it/them
        // First if check filters out "League" tables that don't get highlighted
        if (this.data.length > 1) {
            this.dynamicHighlightHeaders = this.headers
                .filter((d) => d.highlightDynamic)
                .map((h) => h.field)

            if (this.dynamicHighlightHeaders.length) {
                this.hasDynamicHighlights = true
                this.dynamicHighlightDomains = this.getDynamicDomains(
                    this.dynamicHighlightHeaders
                )
            }
        }

        // If defaultSort column passed in and it exists sort by that column initially
        if (this.defaultSort) {
            this.currentSortColumn = this.defaultSort

            this.sortColumns()
        }

        if (this.handleUrlUpdates.enabled) {
            const urlSearchParams = new URLSearchParams(window.location.search)
            this.deserializeTableState(urlSearchParams)
            // Needs to be resorted
            this.sortColumns()
        }

        if (this.data) {
            this.drawTable(true)
            this.addListeners()
        }
    }

    // eslint-disable-next-line class-methods-use-this
    secondaryHeader(
        headers: {
            name: string
            colspan: number
            headerClass: string
        }[]
    ): string {
        let html = '<tr>'
        for (let i = 0; i < headers.length; i++) {
            const header = headers[i]
            let cssClass = ''
            if (header.headerClass) {
                cssClass = ` class="${header.headerClass}" `
            }
            let colspan = 1

            if (header.colspan) {
                colspan = header.colspan
            }
            html += `<th colspan="${colspan}" ${cssClass}>${header.name}</th>`
        }
        html += '</tr>'
        return html
    }

    primaryHeader(headers: Header<Data>[], type: string): string {
        let html = '<tr class="tr-component-row">'
        if (this.showRank) {
            html += `
            <${type} class="th-component-rank th-component-header align-left th-vertical-bottom">
                Rk.${this.isSticky ? '<div>Rk.</div>' : ''}
            </${type}>`
        }

        for (let i = 0; i < headers.length; i++) {
            const header = headers[i]
            let cssClass = ''
            if (header.headerClass) {
                cssClass = ` class="${header.headerClass} ${
                    header.toolTip ? 'tooltip-hover' : ''
                }`

                if (header.sortable === false) {
                    cssClass += ` th-component-header-no-sort `
                }
            }

            if (
                this.currentSortColumn[0].field === header.field ||
                this.currentSortColumn[0].field === header.customSortField
            ) {
                if (cssClass.indexOf('class=') > -1) {
                    cssClass += ` ${this.sortClass}`
                } else {
                    cssClass = ` class="${this.sortClass} ${
                        header.toolTip ? 'tooltip-hover' : ''
                    }`
                }
            }
            if (cssClass) {
                cssClass += '"'
            }

            let toolTip = ''
            if (header.toolTip) {
                toolTip = `<div class="${
                    header.toolTipClass || 'th-tooltip'
                }">${header.toolTip}</div>`
            }

            const thId = `${this.id}_th-${i}`
            html += `
            <${type}
                id="${thId}"
                ${cssClass}
                data-table-header-id="${this.id}"
                data-table-header-idx="${i}"
            >
                ${header.name}
                ${this.isSticky ? `<div>${header.name}</div>` : ''}
                ${toolTip}
            </${type}>`
        }
        html += '</tr>'
        return html
    }

    sortColumns() {
        const reversedSorts = this.currentSortColumn.slice().reverse()

        reversedSorts.forEach(({ field, state }) => {
            const header = this.headers.find(
                (d) => field === d.customSortField || field === d.field
            )

            if (header) {
                header.state = state

                this.handlePreSort(header)

                this.sortColumn(header)
            }
        })
    }

    handlePreSort(header: Header<Data>) {
        if (header.hasLinkedSort && this.existsLinkedSort) {
            const linkedHeader = this.headers.find(
                (h) => h.field === header.linkedSort
            )

            if (!linkedHeader) {
                throw new Error(`no linked header: ${header.linkedSort} found.`)
            }

            // Assume we want the same direction for sanity for now
            linkedHeader.state = header.state

            this.sortColumn(linkedHeader)
        }
    }

    sortColumn(header: Header<Data>) {
        const { type = 'string', state, customSortField, field } = header
        const col = customSortField || field
        const isDesc = state === 'desc'

        this.data = this.data.sort((a, b) => {
            let aVal = this.sortFunction(
                type,
                a,
                col,
                Boolean(this.hasPinned),
                isDesc
            )
            let bVal = this.sortFunction(
                type,
                b,
                col,
                Boolean(this.hasPinned),
                isDesc
            )

            // `sortFunction` will return a maximum/minimum value for pinned rows, to place them at the top.
            // Without the following code those rows will all be tied and won't change order when sorted.
            // Below we check whether we are dealing with two pinned rows and if so set `false` for the `hasPinned` parameter.
            // This will allow the two pinned rows to be compared via the normal `sortFunction` value.
            if (a.pinned && b.pinned && this.hasPinned) {
                aVal = this.sortFunction(type, a, col, false, isDesc)
                bVal = this.sortFunction(type, b, col, false, isDesc)
            }

            if (aVal === bVal) {
                return 0
            }

            if (isDesc) {
                return aVal > bVal ? -1 : 1
            }

            return aVal > bVal ? 1 : -1
        })
    }

    populateRow(data: Data[], isFooter: boolean = false) {
        let tableHTML = ''
        for (let i = 0; i < data.length; i++) {
            if (this.repeatHeader && i % this.repeatHeader === 0 && i > 0) {
                tableHTML += this.primaryHeader(this.headers, 'th')
            }

            if (data[i]) {
                const row = data[i]
                const pinnedRow = this.hasPinned && row.pinned

                let rowId: string | number = i
                let rowStyle = ''
                // Used for AJAX Calls
                if (this.subDataId) {
                    // TODO: Can we do better typing
                    rowId = row[this.subDataId] as string | number

                    if (!this.drawerAvailable || this.drawerAvailable(row)) {
                        rowStyle = ' style="cursor: pointer;" '
                    }
                }
                // Used to inline change table rows (ie, coloring of barrels and low HP%)
                else if (this.uniqueId) {
                    // TODO: Improve typing
                    rowId = row[this.uniqueId] as string | number
                }

                // Append custom class per row if specified in the data set
                let customClass = ''
                // Why is it necessary to pull a variable out in TS? Dunno.
                const customClassValue = row.customClass
                if (customClassValue && typeof customClassValue === 'string') {
                    customClass = customClassValue
                }

                // Append Footer Class if specified
                let footerClass = ''
                if (isFooter && this.footerClass) {
                    footerClass = this.footerClass
                }

                let rowClass = this.rowClass || ''

                rowClass = pinnedRow
                    ? (rowClass += ' savant-table-pinned-row')
                    : rowClass

                tableHTML += `<tr id="${this.id}-tr_${rowId}" class="default-table-row ${rowClass} ${footerClass} ${customClass}" ${rowStyle} data-id="${rowId}">`

                if (this.showRank) {
                    const rank = pinnedRow
                        ? ''
                        : row[SAVANT_TABLE_PAGINATION_RANK_PROP]

                    tableHTML += `<td>${rank}</td>`
                }
                for (let j = 0; j < this.headers.length; j++) {
                    const header = this.headers[j]

                    let cssClass = ''
                    if (header.fieldClass) {
                        cssClass = ` class="${header.fieldClass} `
                    }
                    if (
                        (this.currentSortColumn[0].field === header.field ||
                            this.currentSortColumn[0].field ===
                                header.customSortField) &&
                        !isFooter
                    ) {
                        if (cssClass.indexOf('class=') > -1) {
                            cssClass += ` ${this.sortClass}`
                        } else {
                            cssClass = ` class="${this.sortClass} `
                        }
                    }
                    if (header.footerClass && isFooter) {
                        if (cssClass.indexOf('class=') > -1) {
                            cssClass += ` ${header.footerClass}`
                        } else {
                            cssClass = ` class="${this.sortClass} `
                        }
                    }

                    if (cssClass) {
                        cssClass += '"'
                    }

                    let id = ''
                    // Only allow id to be used on td if it has a uniqueId defined
                    if (header.id && this.uniqueId) {
                        id = ` id="td_${data[i][this.uniqueId]}_${header.id}" `
                    }

                    let fieldData: RowValue = ''

                    if (data[i].hasOwnProperty(header.field)) {
                        fieldData = data[i][header.field]
                    }

                    if (fieldData == null || header.photoOnly === true) {
                        fieldData = ''
                    }

                    if (
                        header.fieldAlt &&
                        typeof header.fieldAlt === 'string'
                    ) {
                        cssClass += ` title="${header.fieldAlt}" `
                    } else if (
                        header.fieldAlt &&
                        typeof header.fieldAlt === 'function'
                    ) {
                        const title = header.fieldAlt(
                            fieldData,
                            data[i],
                            header
                        )
                        cssClass += ` title="${title}" `
                    }

                    let rankingData = ''

                    if (
                        header.ranking !== undefined &&
                        data[i].hasOwnProperty(header.ranking)
                    ) {
                        rankingData = String(data[i][header.ranking])
                    }

                    let photo = ''
                    if (header.photo && header.photoId) {
                        photo = header.photo
                            .split('{1}')
                            .join(String(data[i][header.photoId]))
                    }

                    let background = ''
                    let backgroundColor = ''
                    if (
                        header.highlightDynamic ||
                        (header.highlight && header.highlight.length > 0)
                    ) {
                        if (header.highlight && header.highlight.length === 5) {
                            this.highlightColor = d3
                                .scaleLinear<string>()
                                .clamp(true)
                                .range(
                                    header.highlightColors || [
                                        '#3661AD',
                                        '#3661AD',
                                        '#ffffff',
                                        '#D82129',
                                        '#D82129',
                                    ]
                                )
                        } else if (
                            header.highlightDynamic &&
                            !this.hasDynamicHighlights
                        ) {
                            // Specific case to handle the "league" default gray highlight for a one-row table
                            this.highlightColor = d3
                                .scaleLinear<string>()
                                .clamp(true)
                                .range(['#DCDCDC', '#DCDCDC', '#DCDCDC'])
                        } else {
                            this.highlightColor = d3
                                .scaleLinear<string>()
                                .clamp(true)
                                .range(
                                    header.highlightColors || [
                                        '#3661AD',
                                        '#ffffff',
                                        '#D82129',
                                    ]
                                )
                        }

                        if (
                            header.highlightDynamic &&
                            this.hasDynamicHighlights &&
                            this.dynamicHighlightDomains
                        ) {
                            const highlightDomain =
                                this.dynamicHighlightDomains.find(
                                    (d) => d.field === header.field
                                )?.domain || [0, 1]

                            this.highlightColor.domain(highlightDomain)
                        } else if (header.highlight) {
                            this.highlightColor.domain(header.highlight)
                        }

                        let rankColumn: RowValue = fieldData
                        if (
                            header.rankColumn !== undefined &&
                            data[i].hasOwnProperty(header.rankColumn)
                        ) {
                            rankColumn = data[i][header.rankColumn]
                        }

                        if (
                            this.highlightNullish === false &&
                            rankColumn !== null &&
                            rankColumn !== undefined &&
                            this.highlightValFn
                        ) {
                            const val = this.highlightValFn(
                                String(rankColumn),
                                data[i],
                                data
                            )
                            backgroundColor = this.highlightColor(val)
                            background = `style="background-color: ${backgroundColor}" `
                        } else if (
                            (this.highlightFalsy === false && rankColumn) ||
                            (this.highlightFalsy !== false &&
                                this.highlightNullish !== false)
                        ) {
                            const val = this.highlightValFn(
                                rankColumn,
                                data[i],
                                data
                            )
                            backgroundColor = this.highlightColor(val)
                            background = `style="background-color: ${backgroundColor}" `
                        }
                    }

                    const spanFieldData = this.getSpan(
                        header,
                        data[i],
                        rankingData,
                        fieldData,
                        isFooter,
                        backgroundColor
                    )

                    tableHTML += `<td ${id} ${cssClass} ${background}>${photo} ${spanFieldData}</td>`
                }
                tableHTML += '</tr>'

                if (this.subDataId) {
                    tableHTML += `<tr class="tr-sub-data" id="${
                        this.id
                    }-trSub_${rowId}" style="background-color: #ffffff; display: none;"><td id="${
                        this.id
                    }-tdSub_${rowId}" colspan="${
                        this.headers.length + (this.showRank ? 1 : 0)
                    }" ></td></tr>`
                    // To keep stripe add another row
                    tableHTML += '<tr style="display: none;"></tr>'
                }
            }
        }

        return tableHTML
    }

    getSpan(
        header: Header<Data>,
        data: Data,
        rankingData: string,
        fieldData: RowValue,
        isFooter: boolean,
        backgroundColor: string
    ) {
        let spanStart = '<span'
        const spanEnd = '</span>'

        if (header.spanClass) {
            if (data.playerId && data.season) {
                spanStart += ` id="${
                    Array.isArray(header.spanClass)
                        ? header.spanClass.map((d) => d.className).join(' ')
                        : header.spanClass
                }_${data.playerId}_${data.season}" `
            }

            if (
                header.spanClass.length &&
                header.spanClass.length > 0 &&
                Array.isArray(header.spanClass)
            ) {
                spanStart += ' class="'

                for (let i = 0; i < header.spanClass.length; i++) {
                    const c = header.spanClass[i]
                    if (c.className && c.exception && !isFooter) {
                        if (
                            c.exception === 'gte' &&
                            Number(data[c.field]) >= c.parameter
                        ) {
                            this.showFooterLinks = true
                            spanStart += ` ${c.className}`
                        } else if (
                            c.exception === 'gt' &&
                            Number(data[c.field]) > c.parameter
                        ) {
                            this.showFooterLinks = true
                            spanStart += ` ${c.className}`
                        } else if (
                            c.exception === 'lte' &&
                            Number(data[c.field]) <= c.parameter
                        ) {
                            this.showFooterLinks = true
                            spanStart += ` ${c.className}`
                        } else if (
                            c.exception === 'lt' &&
                            Number(data[c.field]) < c.parameter
                        ) {
                            this.showFooterLinks = true
                            spanStart += ` ${c.className}`
                        } else if (
                            c.exception === 'eq' &&
                            Number(data[c.field]) === c.parameter
                        ) {
                            this.showFooterLinks = true
                            spanStart += ` ${c.className}`
                        }
                    } else if (
                        isFooter &&
                        c.includeFooter &&
                        this.showFooterLinks
                    ) {
                        spanStart += ` ${c.className}`
                    } else if (c.className && !c.exception) {
                        spanStart += ` ${c.className}`
                    }
                }
                spanStart += '"'
            } else {
                spanStart += ` class="${header.spanClass}" `
            }
        }
        if (
            header.ranking &&
            rankingData &&
            typeof this.highlightRanking === 'function'
        ) {
            spanStart += ` ${spanStart} style="padding: 0px 5px 0px 5px; background-color: ${this.highlightRanking(
                Number(rankingData)
            )};" `
        } else if (backgroundColor) {
            spanStart +=
                ` ` +
                ` style="color: ${setTextColorWithBackground(
                    d3.color(backgroundColor)
                )};" `
        }

        spanStart += '>'

        const value = header.displayFn
            ? header.displayFn(fieldData, data, header, isFooter)
            : fieldData
        return spanStart + value + spanEnd
    }

    isEmpty() {
        const footer = this.footer || []
        return !this.filteredData.length && !footer.length
    }

    setPinnedAndMarkRank() {
        this.pinned = this.data.filter((d) => d.pinned)

        this.notPinned = this.data
            .filter((d) => !d.pinned)
            // avoiding handling ties here for now
            .map((d, i) => ({
                ...d,
                [SAVANT_TABLE_PAGINATION_RANK_PROP]: i + 1,
            }))
    }

    handleSearchFilter(haystack: Data[]) {
        if (!this.search.enabled || this.search.searchString.trim() === '') {
            return haystack
        }

        const needle = normalizeAccents(this.search.searchString)
        const columns = this.search.columnsToSearch.length
            ? this.search.columnsToSearch
            : ['name']

        return haystack.filter((d) => {
            const found = columns.some((col) => {
                if (!d[col]) {
                    return false
                }

                const columnValue = normalizeAccents(String(d[col]))

                return columnValue.includes(needle)
            })

            return found
        })
    }

    handleColumnFilters(haystack: Data[]) {
        let dwindlingHaystack = haystack

        for (let i = 0; i < this.filters.length; i++) {
            const filter = this.filters[i]
            dwindlingHaystack = filterColumn(dwindlingHaystack, filter)
        }

        return dwindlingHaystack
    }

    updateColumnFilter(
        column: keyof Data,
        type: FilterType,
        value: (number | string)[] | number | string | boolean
    ) {
        this.pagination.page = 0

        // Remove column (will be replaced)
        this.filters = this.filters.filter((d) => d.column !== column)

        // Trust caller has validated type/value
        this.filters.push({ column, type, value })

        this.drawTable()
    }

    removeColumnFilter(column: keyof Data) {
        this.pagination.page = 0

        // Only one type of filter is currently allowed per column
        this.filters = this.filters.filter((d) => d.column !== column)

        this.drawTable()
    }

    resetFilters() {
        this.pagination.page = 0

        this.resetSearchString()
        this.resetColumnFilters()

        this.drawTable()
    }

    resetSearchString() {
        this.search.searchString = ''
    }

    resetColumnFilters() {
        this.filters = []
    }

    serializeTableState() {
        const { pagination, search, sort, filters } = this.handleUrlUpdates

        const params: {
            sort?: string
            sortDir?: string
            page?: string
            pageSize?: string
            filters?: string
            search?: string
        } = {}

        if (sort) {
            params.sort = this.currentSortColumn[0]?.field.toString() || ''
            params.sortDir = this.currentSortColumn[0]?.state || 'desc'
        }

        if (pagination) {
            params.page = this.pagination.page.toString()
            params.pageSize = this.pagination.rowsPerPage.toString()
        }

        if (filters) {
            params.filters = ''

            if (this.filters.length > 0) {
                params.filters = JSON.stringify(this.filters)
            }
        }
        if (search) {
            params.search = this.search.searchString
        }

        return params
    }

    deserializeTableState(searchParams: URLSearchParams) {
        let sort = searchParams.get('sort')

        if (sort) {
            let header = this.headers.find((d) => d.field === sort)
            const backupSort = this.defaultSort[0].field
            const backupHeader = this.headers.find(
                (d) => d.field === backupSort
            )

            // Give it a college try to sort by a reasonable key
            if (!header && typeof backupSort === 'string') {
                header = backupHeader
                sort = backupSort
            }

            const sortDir =
                (searchParams.get('sortDir') as SortDirection) ||
                header?.defaultSortDir ||
                this.defaultSortDirection
            this.currentSortColumn = [
                { field: sort as keyof Data, state: sortDir },
            ]
        }

        this.pagination.page = Number(searchParams.get('page') || 0)
        this.pagination.rowsPerPage = Number(
            searchParams.get('pageSize') || defaultRowsPerPage
        )

        this.search.searchString = searchParams.get('search') || ''

        // Restore filters
        const filtersParam = searchParams.get('filters')
        if (filtersParam) {
            try {
                this.filters = JSON.parse(filtersParam)
            } catch (e) {
                console.warn('Failed to parse filters from URL:', e)
                this.filters = []
            }
        }
    }

    drawTable(skipUrl = false) {
        this.setPinnedAndMarkRank()

        const filteredRows = this.handleColumnFilters(this.notPinned)

        this.filteredData = this.handleSearchFilter(filteredRows)

        const { page } = this.pagination
        let { rowsPerPage } = this.pagination

        if (!this.pagination.enabled) {
            rowsPerPage = almostInfinity
        }

        this.pagination.pages = Math.max(
            Math.ceil(this.filteredData.length / rowsPerPage),
            1
        )

        const initialPage = page * rowsPerPage
        const pagedData = this.filteredData.slice(
            initialPage,
            initialPage + rowsPerPage
        )

        if (this.secondaryHeaders) {
            this.secondaryHeaderHTML = this.secondaryHeader(
                this.secondaryHeaders
            )
        }
        this.primaryHeaderHTML = this.primaryHeader(this.headers, 'th')

        const pinnedHTML = this.showPinned ? this.populateRow(this.pinned) : ''
        const pagedHTML = this.populateRow(pagedData)

        const tableFooter = this.populateRow(this.footer || [], true)

        const emptyMessage = this.isEmpty()
            ? // Doesn't play that nicely with static columns
              '<tr><td colspan="999999" class="table-no-data-found btn-warning"><em>No Data Found</em></td></tr>'
            : ''

        // preserve search input state... I would rather not have to do this
        const prevInput = this.sel.querySelector<HTMLInputElement>(
            '[data-table-search-input="1"]'
        )
        const hadFocus = prevInput === document.activeElement
        const selStart = prevInput?.selectionStart ?? null
        const selEnd = prevInput?.selectionEnd ?? null
        const prevValue = prevInput?.value

        this.sel.innerHTML = ''

        if (this.search.enabled) {
            const searchBoxHtml = this.createSearchBox()

            this.sel.innerHTML = searchBoxHtml
        }

        if (this.pagination.enabled) {
            const div = this.createPagination()
            this.sel.appendChild(div)
        }

        // A new API to me.
        this.sel.insertAdjacentHTML(
            'beforeend',
            `
        <div class="${this.tableWrapperClass}">
            <table>
                <thead>
                    ${this.secondaryHeaderHTML}
                    ${this.primaryHeaderHTML}
                </thead>
                <tbody>
                    ${pinnedHTML}
                    ${pagedHTML}
                    ${tableFooter}
                    ${emptyMessage}
                </tbody>
            </table>
        </div>`
        )

        if (this.pagination.enabled) {
            const div = this.createPagination()
            this.sel.appendChild(div)
        }
        // restore search input state
        const newInput = this.sel.querySelector<HTMLInputElement>(
            '[data-table-search-input="1"]'
        )
        if (newInput) {
            if (prevValue === this.search.searchString) {
                newInput.value = prevValue
            }
            if (hadFocus) {
                newInput.focus()
                if (selStart !== null && selEnd !== null) {
                    newInput.setSelectionRange(selStart, selEnd)
                }
            }
        }

        if (this.handleUrlUpdates.enabled && !skipUrl) {
            this.updateUrl()
        }
    }

    updateUrl() {
        const url = new URL(window.location.href)

        const newParams = this.serializeTableState()

        Object.entries(newParams).forEach(([key, value]) => {
            url.searchParams.set(key, value)
        })

        window.history.replaceState('', '', url.toString())
    }

    handleHeaderClick($sortEl: HTMLElement, isSecondary: boolean) {
        const idxRaw = $sortEl.dataset.tableHeaderIdx

        if (idxRaw === undefined) {
            throw new Error(`Couldn't find header idx ${$sortEl.id}`)
        }

        const idx = Number(idxRaw)

        const header = this.headers[idx]

        // Make sure it's explicitly set to false
        if (header.sortable === false) {
            return
        }

        const lastIdx = this.currentSortColumn.length - 1
        const relevantSort = this.currentSortColumn[isSecondary ? lastIdx : 0]

        const sortColumn = header.customSortField || header.field
        const isFlip =
            Boolean(relevantSort) && sortColumn === relevantSort.field

        // If it's already set, flip it
        if (isFlip) {
            this.headers[idx].state = header.state === 'asc' ? 'desc' : 'asc'
        } else {
            this.headers[idx].state =
                header.defaultSortDir || this.defaultSortDirection
        }

        const newSort = {
            field: sortColumn,
            state: this.headers[idx].state,
        }

        if (isSecondary) {
            // tack on the end
            this.currentSortColumn = [
                ...this.currentSortColumn.filter(
                    (d) => d.field !== newSort.field
                ),
                newSort,
            ]
        } else {
            this.currentSortColumn = [newSort]
        }

        this.sortColumns()

        this.pagination.page = 0

        this.drawTable()

        if (this.sortCallback) {
            this.sortCallback(
                this.currentSortColumn[0].field,
                idx,
                this.headers[idx],
                this.data,
                this
            )
        }
    }

    findSortEl(target: HTMLElement) {
        return target.closest(
            `[data-table-header-id="${this.id}"]`
        ) as HTMLElement | null
    }

    addListeners() {
        this.sel.addEventListener('click', (e: MouseEvent) => {
            const isSecondary = e.shiftKey
            const target = e.target as HTMLElement | null
            if (!target || !(target instanceof Element)) {
                return
            }
            const $sortEl = this.findSortEl(target)

            if ($sortEl) {
                e.preventDefault()
                this.handleHeaderClick($sortEl, isSecondary)
                return
            }

            const paginationButton = target.closest(
                '[data-table-pagination-button]'
            ) as HTMLElement

            if (paginationButton) {
                if (paginationButton.dataset.tablePaginationButtonIncrement) {
                    this.incrementPage()
                    return
                }

                if (paginationButton.dataset.tablePaginationButtonDecrement) {
                    this.decrementPage()
                    return
                }

                if (paginationButton.dataset.tablePaginationButtonFirst) {
                    this.setPage(0)
                    return
                }

                if (paginationButton.dataset.tablePaginationButtonLast) {
                    this.setPage(this.pagination.pages - 1)
                }

                if (paginationButton.dataset.tablePaginationButton) {
                    const page =
                        Number(paginationButton.dataset.tablePaginationButton) -
                        1

                    this.setPage(page)
                }
            }
        })

        this.sel.addEventListener('change', (e) => {
            const target = e.target as HTMLSelectElement | null

            if (!target) {
                return
            }

            if (target.dataset[TABLE_SELECT_PAGE_SIZE_DATA_STRING]) {
                this.changeRowsPerPage(
                    target.value ? Number(target.value) : defaultRowsPerPage
                )
            }
        })

        this.sel.addEventListener('input', (e) => {
            const target = e.target as HTMLInputElement | null

            if (!target) {
                return
            }

            if (target.dataset.tableSearchInput) {
                this.pagination.page = 0
                this.search.searchString = target.value

                this.drawTable()
            }
        })
    }

    initOpts(initOpts: TableOpts<Data, Table<Data>>) {
        if (initOpts.data) {
            this.data = initOpts.data
        }
        if (initOpts.headers) {
            this.headers = initOpts.headers
        }
        if (initOpts.secondaryHeaders) {
            this.secondaryHeaders = initOpts.secondaryHeaders
        }
        if (initOpts.defaultSort) {
            if (typeof initOpts.defaultSort === 'string') {
                // Thanks TS, you're dumb
                const fieldKey = initOpts.defaultSort as keyof Data

                const header = this.headers.find(
                    (d) =>
                        fieldKey === d.customSortField || fieldKey === d.field
                )

                if (!header) {
                    // TODO: Is this too extreme to fail this way? Probably.
                    throw new Error("couldn't find sort")
                }

                // This is done to match the way it currently works, I think.
                const state =
                    initOpts.initialSortDirection ||
                    initOpts.defaultSortDirection ||
                    header?.defaultSortDir ||
                    'desc'

                // eslint-disable-next-line no-param-reassign
                initOpts.defaultSort = [{ field: fieldKey, state }]
            }

            this.defaultSort = initOpts.defaultSort as Sort<Data>[]
            this.currentSortColumn = initOpts.defaultSort as Sort<Data>[]
        }
        if (initOpts.defaultSortDirection) {
            this.defaultSortDirection = initOpts.defaultSortDirection
        } else {
            this.defaultSortDirection = 'desc'
        }

        if (initOpts.initialSortDirection) {
            this.initialSortDirection = initOpts.initialSortDirection
        }
        if (initOpts.subDataId) {
            this.subDataId = initOpts.subDataId
        }
        if (initOpts.drawerAvailable) {
            this.drawerAvailable = initOpts.drawerAvailable
        }
        if (initOpts.uniqueId) {
            this.uniqueId = initOpts.uniqueId
        }
        if (typeof initOpts.showRank === 'boolean') {
            this.showRank = initOpts.showRank
        }
        if (initOpts.repeatHeader) {
            this.repeatHeader = initOpts.repeatHeader
        }

        this.hasPinned = Boolean(initOpts.hasPinned)

        if (initOpts.isSticky) {
            this.isSticky = initOpts.isSticky
        }

        if (initOpts.footer) {
            this.footer = initOpts.footer
        }
        if (initOpts.sortClass !== undefined) {
            this.sortClass = initOpts.sortClass
        }
        if (initOpts.footerClass) {
            this.footerClass = initOpts.footerClass
        }
        if (initOpts.sortCallback) {
            this.sortCallback = initOpts.sortCallback
        }

        if (initOpts.rowClass) {
            this.rowClass = initOpts.rowClass
        }

        if (initOpts.showPinned !== undefined) {
            this.showPinned = initOpts.showPinned
        }

        if (initOpts.tableWrapperClass) {
            this.tableWrapperClass = initOpts.tableWrapperClass
        }

        if (initOpts.handleUrlUpdates) {
            this.handleUrlUpdates = {
                ...this.handleUrlUpdates,
                ...initOpts.handleUrlUpdates,
            }
        }

        if (initOpts.pagination) {
            this.pagination = { ...this.pagination, ...initOpts.pagination }
        }

        if (initOpts.search) {
            this.search = { ...this.search, ...initOpts.search }
        }

        this.highlightValFn =
            initOpts.highlightValFn || ((val: RowValue) => Number(val))

        // default is true, make true unless explicit
        this.highlightFalsy = initOpts.highlightFalsy !== false
        this.highlightNullish = initOpts.highlightNullish !== false

        this.existsLinkedSort =
            this.headers.filter((header) => header.isLinkedSort).length > 0
    }

    refreshTable() {
        this.sortColumns()
        this.drawTable()
    }

    getDynamicDomains(headers: (keyof Data)[]) {
        const { data } = this

        return headers.map((h) => {
            const [min, max] = d3.extent(data, (d) => Number(d[h]))

            if (min === undefined || max === undefined) {
                throw new Error('no min max found')
            }

            let valRange = []

            // A somewhat rough way to handle this, but we'll see how it looks
            if (min * max > 0) {
                const mid = (min + max) / 2
                const diff = 0.8 * (mid - min)
                valRange = [mid - diff, mid, mid + diff]
            } else {
                const extreme =
                    Math.abs(min) > Math.abs(max)
                        ? Math.abs(min)
                        : Math.abs(max)
                valRange = [-0.8 * extreme, 0, 0.8 * extreme]
            }

            return { field: h, domain: valRange }
        })
    }

    // eslint-disable-next-line class-methods-use-this
    sortFunction(
        type: string,
        row: Data,
        sortColumn: keyof Data,
        hasPinned: boolean,
        isDesc: boolean = false
    ) {
        // Need to be lowercase for comparison sake...I think.
        const almostMaxString = 'zzzzzzzzzzzzzzzzzzzzzzzzzzzz'
        const maxestString = `${almostMaxString}z`
        let value
        if (row) {
            if (hasPinned && row.pinned && type === 'number') {
                return isDesc
                    ? Number.MAX_SAFE_INTEGER
                    : Number.MIN_SAFE_INTEGER
            }

            if (hasPinned && row.pinned) {
                return isDesc ? maxestString : ''
            }

            if (type === 'number') {
                value = row[sortColumn]

                // sort nulls last, picked random large number
                if (value === null || value === undefined) {
                    value = isDesc
                        ? Number.MIN_SAFE_INTEGER + 1
                        : Number.MAX_SAFE_INTEGER - 1
                }

                return Number(String(value).replace('%', '').replace(',', ''))
            }

            if (!row[sortColumn]) {
                // Lowest unicode character vs. a slightly smaller string
                return isDesc ? '\u0000' : almostMaxString
            }

            return String(row[sortColumn]).toLowerCase()
        }

        return isDesc
            ? Number.MAX_SAFE_INTEGER - 1
            : Number.MIN_SAFE_INTEGER + 1
    }

    // This whole thing feels dirty
    createPagination() {
        const div = document.createElement('div')
        div.classList.add('table-pagination')

        const divLeft = document.createElement('div')
        divLeft.classList.add('table-pagination-wrapper-left')

        const paginationButtons = this.createPaginationButtons()

        divLeft.innerHTML = paginationButtons

        const { rowsPerPage, page } = this.pagination

        const initialRow = page * rowsPerPage + 1
        const lastPage = Math.min(
            initialRow + rowsPerPage - 1,
            this.filteredData.length
        )

        const divRight = document.createElement('div')
        divRight.classList.add('table-pagination-wrapper-right')
        let showingTextContent = this.filteredData.length
            ? `Showing ${initialRow}-${lastPage} of ${this.filteredData.length} results`
            : 'No Results'

        if (this.filteredData.length !== this.notPinned.length) {
            showingTextContent += ` - <em>(filtered from ${this.notPinned.length})</em>`
        }

        divRight.innerHTML = `<div class="table-pagination-showing-text">${showingTextContent}</div>`

        const savantSelect = this.createPageSizeSelect()

        divRight.insertAdjacentHTML('beforeend', savantSelect)

        div.appendChild(divLeft)
        div.appendChild(divRight)

        return div
    }

    // A row of clickable buttons with first/last/increment/decrement
    createPaginationButtons() {
        const visualPage = this.pagination.page + 1
        const totalBoxes = 9
        const halfUp = Math.ceil(totalBoxes / 2)
        // const halfDown = Math.floor(totalBoxes / 2)
        // An ellipsis box on either side
        const defaultBoxesToShow = totalBoxes - 2

        let startPage = visualPage - (halfUp - 2)
        let endPage = startPage + defaultBoxesToShow - 1

        let showFirstEllipsis = true
        let showLastEllipsis = true

        if (this.pagination.pages <= totalBoxes) {
            startPage = 1
            endPage = this.pagination.pages
            showFirstEllipsis = false
            showLastEllipsis = false
        } else if (startPage <= 2) {
            startPage = 1
            endPage = startPage + defaultBoxesToShow
            showFirstEllipsis = false
        } else if (endPage >= this.pagination.pages - 1) {
            endPage = this.pagination.pages
            startPage = endPage - defaultBoxesToShow
            showLastEllipsis = false
        }

        let currentPage = startPage

        let buttons = ''

        while (currentPage <= endPage) {
            const isCurrent = currentPage === this.pagination.page + 1
            const className = isCurrent
                ? 'table-pagination-button table-pagination-button-current'
                : 'table-pagination-button'
            buttons += `
            <div class="${className}" data-table-pagination-button="${currentPage}">${currentPage}</div>
            `
            currentPage += 1
        }

        const canDecrement = this.pagination.page !== 0 ? 1 : 0
        const canIncrement =
            this.pagination.page + 1 < this.pagination.pages ? 1 : 0

        const html = `
        <div class="table-pagination-arrows">
            <div class="table-pagination-button" data-table-pagination-button="1" data-table-pagination-button-is-active="${canDecrement}">
                ${fastRewindIcon}
            </div>
            <div class="table-pagination-button" data-table-pagination-button-is-active="${canDecrement}" data-table-pagination-button="${
            this.pagination.page
        }">
                ${rewindIcon}
            </div>
            ${
                showFirstEllipsis
                    ? '<div class="table-pagination-button table-pagination-button-ellipsis">...</div>'
                    : ''
            }
            ${buttons}
            ${
                showLastEllipsis
                    ? '<div class="table-pagination-button table-pagination-button-ellipsis">...</div>'
                    : ''
            }
            <div class="table-pagination-button" data-table-pagination-button-is-active="${canIncrement}" data-table-pagination-button="${
            this.pagination.page + 2
        }">
                ${forwardIcon}
            </div>
            <div class="table-pagination-button" data-table-pagination-button-is-active="${canIncrement}" data-table-pagination-button="${
            this.pagination.pages
        }">
                ${fastForwardIcon}
            </div>
        </div>`

        return html
    }

    createPageSizeSelect() {
        const options = pageOptions
            // Remove options that are far too big
            .filter(
                (d, i) => i === 0 || pageOptions[i - 1] < this.notPinned.length
            )
            .map((num) => {
                const selectedStr =
                    this.pagination.rowsPerPage === num ? 'selected' : ''

                return `
                <option value="${num}" ${selectedStr}>${
                    num === almostInfinity ? '∞' : num
                }</option>`
            })
            .join('')

        const html = `
        <div class="savant-select" data-title="Page Size">
            <select data-table-pagination-page-size="1">
                ${options}
            </select>
        </div>
        `

        return html
    }

    setPage(pageNumber: number = 0) {
        if (pageNumber < 0) {
            this.pagination.page = 0
        } else if (pageNumber >= this.pagination.pages) {
            this.pagination.page = this.pagination.pages - 1
        } else {
            this.pagination.page = pageNumber
        }

        this.drawTable()
    }

    incrementPage() {
        // Zero index messing with me on the - 1
        if (this.pagination.page < this.pagination.pages - 1) {
            this.pagination.page += 1
            this.drawTable()
        }
    }

    decrementPage() {
        if (this.pagination.page > 0) {
            this.pagination.page -= 1
            this.drawTable()
        }
    }

    changeRowsPerPage(rowsPerPage: number) {
        this.pagination.page = 0
        this.pagination.rowsPerPage = rowsPerPage
        this.drawTable()
    }

    updateData(data: Data[], footer?: Data[], refreshPage = true) {
        this.data = data
        this.footer = footer || this.footer

        this.sortColumns()

        if (refreshPage) {
            this.pagination.page = 0
        }

        this.drawTable()
    }

    setShowPinned(showPinned: boolean) {
        this.showPinned = showPinned
        this.drawTable()
    }

    createSearchBox() {
        return `
        <div style="display: flex; justify-content: end;">
            <input
            data-table-search-input="1"
            class="table-search"
            type="text"
            value="${this.search.searchString}"
            placeholder="${this.search.placeholder}"
            />
        </div>
        `
    }
}
